import React from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLegalDocument, LegalSlug } from '@/hooks/useLegalDocument';
import { Skeleton } from '@/components/ui/skeleton';

interface Props {
  slug: LegalSlug;
}

const LegalDocumentPage: React.FC<Props> = ({ slug }) => {
  const { data, isLoading } = useLegalDocument(slug);

  const html = React.useMemo(() => {
    if (!data?.content) return '';
    const raw = marked.parse(data.content, { async: false }) as string;
    return DOMPurify.sanitize(raw);
  }, [data?.content]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-grow max-w-3xl w-full mx-auto px-6 py-16">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-1/2" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : !data ? (
          <div className="text-center text-gray-500 py-24">Document not found.</div>
        ) : (
          <article
            className="prose prose-slate max-w-none prose-headings:font-semibold prose-a:text-purple-700"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </main>
      <Footer />
    </div>
  );
};

export default LegalDocumentPage;
